import { IconProp } from '@fortawesome/fontawesome-svg-core';
import clsx from 'clsx';
import React, { createElement, CSSProperties, FC, useMemo } from 'react';
import { useThemeColorPalette } from '../../hooks/use-theme-color-palette';
import { ThemeColors } from '../../theme';
import { Icon } from '../icon';
import { Typography } from '../typography';
import classes from './avatar.module.scss';

export type AvatarSize = 'small' | 'medium' | 'large';

export type AvatarProps = {
  /**
   * The size of the avatar, 'medium' is the default
   */
  size?: AvatarSize;

  /**
   * The URL for the avatar image
   */
  url?: string;

  /**
   * The alt value for the image
   */
  alt?: string;

  /**
   * Custom initials to display, instead of the avatar image or the fallback
   */
  initials?: string;

  /**
   * Customize the avatar color appearance
   */
  color?: ThemeColors;

  /**
   * The name of the class.
   */
  className?: string;

  /**
   * The icon to be displayed. Example: ['fas', 'user'], ['fab', 'github']
   */
  icon?: IconProp;

  /**
  * Callback function to be called when the avatar is clicked. If provided, the avatar will be rendered as a button.
  */
  onClick?: () => void;
};

const sizeMap: Record<AvatarSize, string> = {
  small: '38px',
  medium: '48px',
  large: '80px' // the initial size of the avatar
};

const fontSizeMap: Record<AvatarSize, string> = {
  small: 'var(--theme--sizing--2x)',
  medium: 'var(--theme--sizing--2x)',
  large: 'var(--theme--sizing--3x)'
};

export const Avatar: FC<AvatarProps> = ({
  className,
  alt,
  url,
  color,
  initials,
  icon,
  size = 'medium',
  onClick,
  ...props
}) => {
  const palette = useThemeColorPalette(color ?? null);

  const as = useMemo(() => {
    return onClick ? 'button' : 'div';
  }, [onClick]);

  return createElement(as, {
    ...props,
    className: clsx('avatar-component', classes.root, className, { [classes.isButton]: !!onClick }),
    onClick,
    style: {
      '--avatar--background': palette?.['ultra-light'],
      '--avatar--foreground': palette?.main,
      '--avatar--size': sizeMap[size],
      '--avatar--font-size': fontSizeMap[size]
    } as CSSProperties
  }, <>
    {initials && <Typography className={classes.initials} as='span'>{initials}</Typography>}
    {url && <img className={classes.visual} src={url} alt={alt ?? ''} />}
    {!url && !initials && <Icon className={classes.icon} icon={icon ?? 'user'} />}

  </>);
};